/**
 * Production asset loading utilities.
 * - DRACO enabled for GLB (70–90% size reduction)
 * - Max texture size 2048 to prevent GPU overload
 * - KTX2-ready (design textures remain PNG/JPG for catalog; 3D can use KTX2)
 */
import * as THREE from "three";
import { useGLTF } from "@react-three/drei";
import { assetUrl, DRACO_DECODER } from "../config/assets";

// Set DRACO decoder path once (CDN; no local copy needed)
if (typeof useGLTF?.setDecoderPath === "function") {
  useGLTF.setDecoderPath(DRACO_DECODER);
}

/** Max texture dimension for GPU safety (avoid 4K+ on low-end devices) */
export const MAX_TEXTURE_SIZE = 2048;

/** Cap texture size; GPU-friendly */
function capTextureSize(texture) {
  if (!texture?.image) return texture;
  const w = texture.image.width || 0;
  const h = texture.image.height || 0;
  if (w <= MAX_TEXTURE_SIZE && h <= MAX_TEXTURE_SIZE) return texture;
  const scale = Math.min(MAX_TEXTURE_SIZE / w, MAX_TEXTURE_SIZE / h, 1);
  const nw = Math.max(1, Math.floor(w * scale));
  const nh = Math.max(1, Math.floor(h * scale));
  const canvas = document.createElement("canvas");
  canvas.width = nw;
  canvas.height = nh;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(texture.image, 0, 0, nw, nh);
  texture.image = canvas;
  texture.needsUpdate = true;
  return texture;
}

/** Create TextureLoader with max size cap */
export function createTextureLoader(capSize = true) {
  const loader = new THREE.TextureLoader();
  const originalLoad = loader.load.bind(loader);
  loader.load = function (url, onLoad, onProgress, onError) {
    const wrappedOnLoad = (tex) => {
      if (tex && capSize) capTextureSize(tex);
      if (onLoad) onLoad(tex);
    };
    const resolved = url ? assetUrl(url.startsWith("/") ? url : `/${url}`) : url;
    return originalLoad(resolved || url, wrappedOnLoad, onProgress, onError);
  };
  return loader;
}

/** Shared texture loader instance (capped) */
let _textureLoader = null;
export function getTextureLoader() {
  if (!_textureLoader) _textureLoader = createTextureLoader(true);
  return _textureLoader;
}

/** Resolve design/tile image URL for loading */
export function resolveDesignUrl(path) {
  if (!path) return "";
  const p = path.replace(/^\.\/|^\.\.\//g, "").replace(/\\/g, "/").trim();
  const normalized = p.startsWith("/") ? p : `/${p}`;
  return assetUrl(normalized);
}

/** Dispose geometry, materials, textures recursively */
export function disposeObject(obj) {
  if (!obj) return;
  obj.traverse((child) => {
    if (child.geometry) {
      child.geometry.dispose();
      child.geometry = null;
    }
    if (child.material) {
      const mats = Array.isArray(child.material) ? child.material : [child.material];
      mats.forEach((m) => {
        if (m.map) m.map.dispose();
        if (m.normalMap) m.normalMap.dispose();
        if (m.roughnessMap) m.roughnessMap.dispose();
        if (m.metalnessMap) m.metalnessMap.dispose();
        m.dispose();
      });
      child.material = null;
    }
  });
}
